/**
 * CSV export utilities
 */

export type CSVColumn<T> = {
  header: string;
  getValue: (item: T) => string | number | null | undefined;
};

export type CSVExportOptions = {
  filename?: string;
};

/**
 * Sanitizes user-provided input to prevent Excel/CSV formula injection.
 * Prefixes dangerous characters with a single quote so Excel treats them as text.
 *
 * Use this for data that comes from user input (e.g., submission_referees fields,
 * submission_entries.person_name). Do NOT use for system data or already-validated
 * fields like email addresses.
 */
export function sanitizeUserInput(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) {
    return "'" + str;
  }
  return str;
}

export function escapeCsvValue(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function downloadCsv(csvContent: string, filename: string) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, filename);
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function generateCsv<T>(
  data: T[],
  headers: string[],
  rowMapper: (item: T) => string[],
): string {
  const rows = data.map(item => rowMapper(item).map(val => escapeCsvValue(val)));
  return [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
}

export function exportToCsv<T>(
  data: T[],
  columns: CSVColumn<T>[],
  options: CSVExportOptions = {},
): void {
  const headers = columns.map(col => col.header);
  const rows = data.map(item =>
    columns.map(col => {
      const value = col.getValue(item);
      return escapeCsvValue(value === null || value === undefined ? "" : String(value));
    }),
  );

  const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
  const filename = options.filename
    ? `${options.filename}-${new Date().toISOString().split("T")[0]}.csv`
    : `export-${new Date().toISOString().split("T")[0]}.csv`;

  downloadCsv(csvContent, filename);
}
